<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="820" persistent scrollable
        content-class="ms-dialog-wrap">
        <div class="ms-dialog">
            <!-- HEADER -->
            <header class="ms-dlg-header">
                <div class="ms-dlg-header__main">
                    <span class="ms-dlg-header__num">#{{ itemToEdit.request_number || '—' }}</span>
                    <div>
                        <h2 class="ms-dlg-header__title">עריכת פרטי קריאה</h2>
                        <span class="ms-dlg-header__sub">{{ customerName }}</span>
                    </div>
                </div>
                <button type="button" class="ms-dlg-close" @click="close" aria-label="סגירה">
                    <v-icon size="20">mdi-close</v-icon>
                </button>
            </header>

            <!-- BODY -->
            <div class="ms-dlg-body">
                <v-form ref="form" v-model="formValid" @submit.prevent="save">
                    <!-- סטטוס ודחיפות -->
                    <section class="ms-card">
                        <h3 class="ms-card__title">
                            <v-icon size="17">mdi-tune-variant</v-icon>
                            סטטוס ודחיפות
                        </h3>

                        <div class="ms-field-label">סטטוס הקריאה</div>
                        <div class="ms-choice-row">
                            <button v-for="s in statusOptions" :key="s.value" type="button" class="ms-choice"
                                :class="[`ms-choice--${s.modifier}`, { 'ms-choice--active': form.status === s.value }]"
                                @click="form.status = s.value">
                                <v-icon size="16">{{ s.icon }}</v-icon>
                                {{ s.label }}
                            </button>
                        </div>

                        <div class="ms-field-label">רמת דחיפות</div>
                        <div class="ms-choice-row">
                            <button v-for="p in priorityOptions" :key="p.value" type="button" class="ms-choice"
                                :class="[`ms-choice--${p.modifier}`, { 'ms-choice--active': form.priority === p.value }]"
                                @click="form.priority = p.value">
                                <v-icon size="16">{{ p.icon }}</v-icon>
                                {{ p.label }}
                            </button>
                        </div>

                        <v-textarea v-if="statusChanged" v-model="form.statusNote" outlined dense rows="2"
                            hide-details="auto" class="ms-input ms-status-note"
                            :label="`הערה לשינוי הסטטוס ל״${statusLabel(form.status)}״ (לא חובה)`"
                            prepend-inner-icon="mdi-comment-text-outline" />
                    </section>

                    <!-- פרטי הקריאה -->
                    <section class="ms-card">
                        <h3 class="ms-card__title">
                            <v-icon size="17">mdi-clipboard-text-outline</v-icon>
                            פרטי הקריאה
                        </h3>
                        <div class="ms-grid">
                            <v-select v-model="form.type" :items="typeSelectItems" item-text="label"
                                item-value="value" label="סוג הקריאה" outlined dense hide-details="auto"
                                class="ms-input" />

                            <v-autocomplete v-model="form.assigned_to" :items="usersList" item-text="name" item-value="_id"
                                label="מטפל בקריאה" outlined dense clearable hide-details="auto" class="ms-input"
                                :loading="usersLoading" no-data-text="לא נמצאו משתמשים" />

                            <v-text-field v-model="form.subject" label="נושא" outlined dense hide-details="auto"
                                class="ms-input ms-input--wide" counter="120" :rules="[rules.maxSubject]" />

                            <v-textarea v-model="form.description" label="תיאור" outlined dense rows="4"
                                hide-details="auto" class="ms-input ms-input--wide" />

                            <v-textarea v-model="form.internal_notes" label="הערה פנימית (לא נשלחת לספק)" outlined
                                dense rows="2" hide-details="auto" class="ms-input ms-input--wide" />
                        </div>
                    </section>

                    <!-- מספרים וניוד -->
                    <section class="ms-card">
                        <h3 class="ms-card__title">
                            <v-icon size="17">mdi-cellphone-cog</v-icon>
                            מספרים וניוד
                        </h3>
                        <div class="ms-grid">
                            <v-text-field v-model="form.line_number" label="מספר קו" outlined dense hide-details="auto"
                                class="ms-input ms-input--ltr" :rules="[rules.phone]"
                                :hint="form.type === 'new_line' ? 'יתמלא כשהספק יקצה מספר' : ''" persistent-hint />

                            <v-text-field v-model="form.temp_number" label="מספר זמני (ניוד)" outlined dense
                                hide-details="auto" class="ms-input ms-input--ltr" :rules="[rules.phone]" />

                            <v-text-field v-model="form.sim_number" label="מספר סים" outlined dense hide-details="auto"
                                class="ms-input ms-input--ltr" />

                            <v-text-field v-model="form.abandoned_company" label="חברה נטושה" outlined dense
                                hide-details="auto" class="ms-input" />

                            <v-select v-model="form.supplier_name" :items="supplierSelectItems" item-text="label"
                                item-value="value" label="ספק / מפעיל" outlined dense clearable
                                hide-details="auto" class="ms-input" />

                            <v-menu v-model="sentDateMenu" :close-on-content-click="false" offset-y min-width="290">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="form.sent_to_supplier_at" v-bind="attrs" v-on="on" readonly
                                        label="נשלח לספק בתאריך" outlined dense hide-details="auto" clearable
                                        class="ms-input" prepend-inner-icon="mdi-calendar"
                                        @click:clear="form.sent_to_supplier_at = null" />
                                </template>
                                <v-date-picker v-model="form.sent_to_supplier_at" locale="he-IL" color="#0d2c6d"
                                    no-title @input="sentDateMenu = false" />
                            </v-menu>
                        </div>
                    </section>

                    <div v-if="errorText" class="ms-error">
                        <v-icon size="17">mdi-alert-circle-outline</v-icon>
                        {{ errorText }}
                    </div>
                </v-form>
            </div>

            <!-- FOOTER -->
            <footer class="ms-dlg-footer">
                <button type="button" class="ms-btn ms-btn--ghost" :disabled="saving" @click="close">ביטול</button>
                <button type="button" class="ms-btn ms-btn--primary" :disabled="saving || !isDirty" @click="save">
                    <v-progress-circular v-if="saving" indeterminate size="15" width="2" color="#fff" />
                    <v-icon v-else size="16">mdi-content-save-outline</v-icon>
                    {{ saving ? 'שומר…' : 'שמירת שינויים' }}
                </button>
                <span v-if="!isDirty && !saving" class="ms-footer-hint">אין שינויים לשמירה</span>
            </footer>
        </div>
    </v-dialog>
</template>

<script>
import ApiServices from '@/services/api.service'
import SRConsts from '@/util/serviceRequestsConsts'
import _ from 'lodash'

export default {
    props: {
        value: { type: Boolean, default: false },
        itemToEdit: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            formValid: true,
            saving: false,
            errorText: '',
            sentDateMenu: false,
            usersList: [],
            usersLoading: false,
            originalForm: {},
            form: {
                status: 'open',
                priority: 'normal',
                type: 'new_line',
                subject: '',
                description: '',
                internal_notes: '',
                line_number: '',
                temp_number: '',
                sim_number: '',
                abandoned_company: '',
                supplier_name: '',
                sent_to_supplier_at: null,
                assigned_to: null,
                statusNote: '',
            },
            statusOptions: SRConsts.statusOptions,
            priorityOptions: SRConsts.priorityOptions,
            typeSelectItems: SRConsts.typeOptions,
            supplierSelectItems: SRConsts.supplierOptions,
            rules: {
                maxSubject: v => !v || v.length <= 120 || 'עד 120 תווים',
                phone: v => !v || /^[0-9+\-\s]{6,20}$/.test(v) || 'מספר לא תקין',
            },
        };
    },
    computed: {
        customerName() {
            const c = this.itemToEdit.customer_id;
            if (!c) return '';
            return `${c.name || ''} ${c.family || ''}`.trim();
        },
        statusChanged() {
            return this.form.status !== this.originalForm.status;
        },
        isDirty() {
            return !_.isEqual(this.cleanForm(this.form), this.cleanForm(this.originalForm));
        },
    },
    methods: {
        statusLabel: SRConsts.statusLabel,

        hydrate() {
            const i = this.itemToEdit || {};
            this.form = {
                status: i.status || 'open',
                priority: i.priority || 'normal',
                type: i.type || 'new_line',
                subject: i.subject || '',
                description: i.description || '',
                internal_notes: i.internal_notes || '',
                line_number: i.line_number || '',
                temp_number: i.temp_number || '',
                sim_number: i.sim_number || '',
                abandoned_company: i.abandoned_company || '',
                supplier_name: i.supplier_name || '',
                sent_to_supplier_at: i.sent_to_supplier_at ? i.sent_to_supplier_at.substring(0, 10) : null,
                assigned_to: (i.assigned_to && i.assigned_to._id) || i.assigned_to || null,
                statusNote: '',
            };
            this.originalForm = Object.assign({}, this.form);
        },
        cleanForm(f) {
            // statusNote אינו שדה של הקריאה, לא נחשב לשינוי בפני עצמו
            const { statusNote, ...rest } = f;
            return rest;
        },
        async getUsers() {
            try {
                this.usersLoading = true;
                const token = localStorage.getItem("token");
                const api = process.env.VUE_APP_BASE_URL + `/users/get_users`;
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();
                this.usersLoading = false;

                if (res.status !== 200) {
                    this.usersList = [];
                    this.errorText = 'לא ניתן לטעון את רשימת המטפלים';
                    return;
                }

                // התשובה יכולה להגיע כמערך ישיר או עטופה ב-data / users
                const raw = Array.isArray(jsonObject)
                    ? jsonObject
                    : (jsonObject.data || jsonObject.users || []);

                // ממפים לשדות שצריך בלבד – לא מחזיקים בזיכרון סיסמאות וטוקנים
                this.usersList = raw
                    .filter(u => u && u._id && u.name && u.status !== false)
                    .map(u => ({ _id: u._id, name: u.name }));

            } catch (error) {
                this.usersLoading = false;
                this.usersList = [];
                this.errorText = 'לא ניתן לטעון את רשימת המטפלים';
            }
        },
        async save() {
            this.errorText = '';
            if (!this.$refs.form.validate()) {
                this.errorText = 'יש שדות עם ערך לא תקין — בדוק את המסומנים באדום.';
                return;
            }
            try {
                this.saving = true;
                const token = localStorage.getItem("token");
                const api = process.env.VUE_APP_BASE_URL + `/service_requests/update_service_request`;

                const payload = Object.assign({}, this.cleanForm(this.form), {
                    _id: this.itemToEdit._id,
                    sent_to_supplier_at: this.form.sent_to_supplier_at || null,
                });
                if (this.statusChanged && this.form.statusNote) {
                    payload.status_note = this.form.statusNote;
                }

                const res = await fetch(api, ApiServices.requestOptions("PUT", JSON.stringify(payload), token));
                const jsonObject = await res.json();
                this.saving = false;

                if (res.status >= 400) {
                    this.errorText = (jsonObject && jsonObject.message) || 'שמירת השינויים נכשלה. נסה שוב.';
                    return;
                }
                // השרת מחזיר את הקריאה המעודכנת (עם populate) כדי שהטבלה תתעדכן בלי רענון
                this.$emit('itemEdited', jsonObject.data || payload, 'הקריאה עודכנה בהצלחה', 'green');
                this.$emit('input', false);
            } catch (error) {
                this.saving = false;
                this.errorText = 'שגיאת תקשורת: ' + error;
            }
        },
        close() {
            if (this.isDirty && !window.confirm('יש שינויים שלא נשמרו. לצאת בכל זאת?')) return;
            this.$emit('input', false);
        },
    },
    created() {
        this.hydrate();
        this.getUsers();
    },
}
</script>

<style scoped>
.ms-dialog {
    --m-navy: #0d2c6d;
    --m-indigo: #3F51B5;
    --m-indigo-soft: rgba(63, 81, 181, 0.10);
    --m-blue: #1976d2;
    --m-blue-soft: rgba(25, 118, 210, 0.10);
    --m-border: #e3e8ef;
    --m-border-soft: #eef2f7;
    --m-text: #0f172a;
    --m-text-soft: #475569;
    --m-text-mute: #94a3b8;
    --m-green: #16804b;
    --m-green-soft: rgba(22, 128, 75, 0.10);
    --m-orange: #c0691c;
    --m-orange-soft: #fff3df;
    --m-red: #c74747;
    --m-red-soft: rgba(199, 71, 71, 0.10);
    --m-slate: #64748b;
    --m-slate-soft: rgba(100, 116, 139, 0.10);
    --m-ease: cubic-bezier(0.22, 1, 0.36, 1);
    display: flex;
    flex-direction: column;
    max-height: 92vh;
    background: #f7f9fc;
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, 'Segoe UI', sans-serif;
}

/* ── HEADER ── */
.ms-dlg-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 15px 20px;
    background: linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
}

.ms-dlg-header__main {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.ms-dlg-header__num {
    flex-shrink: 0;
    padding: 5px 11px;
    border-radius: 9px;
    background: rgba(253, 183, 33, 0.20);
    border: 1px solid rgba(253, 183, 33, 0.38);
    color: #ffd77a;
    font-size: 0.92rem;
    font-weight: 800;
    font-feature-settings: "tnum" 1;
}

.ms-dlg-header__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.3;
}

.ms-dlg-header__sub {
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.79rem;
    font-weight: 600;
}

.ms-dlg-close {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.10);
    border: none;
    cursor: pointer;
    transition: background 0.15s var(--m-ease);
}

.ms-dlg-close:hover {
    background: rgba(255, 255, 255, 0.22);
}

.ms-dlg-close ::v-deep .v-icon {
    color: #fff !important;
}

/* ── BODY ── */
.ms-dlg-body {
    flex: 1;
    overflow-y: auto;
    padding: 18px;
}

.ms-card {
    padding: 14px 16px 17px;
    margin-bottom: 14px;
    border-radius: 13px;
    background: #fff;
    border: 1px solid var(--m-border-soft);
    box-shadow: 0 1px 3px rgba(13, 44, 109, 0.05);
}

.ms-card:last-of-type {
    margin-bottom: 0;
}

.ms-card__title {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0 0 14px;
    padding-bottom: 9px;
    border-bottom: 1px solid var(--m-border-soft);
    color: var(--m-navy);
    font-size: 0.9rem;
    font-weight: 800;
}

.ms-card__title ::v-deep .v-icon {
    color: var(--m-indigo) !important;
}

/* ── CHOICE BUTTONS ── */
.ms-field-label {
    margin-bottom: 7px;
    color: var(--m-text-mute);
    font-size: 0.78rem;
    font-weight: 700;
}

.ms-choice-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
}

.ms-choice-row:last-child {
    margin-bottom: 0;
}

.ms-choice {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 15px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid var(--m-border);
    color: var(--m-text-soft);
    font-family: inherit;
    font-size: 0.83rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-choice:hover {
    border-color: var(--m-text-mute);
    background: #f8fafc;
}

.ms-choice ::v-deep .v-icon {
    color: currentColor !important;
}

.ms-choice--active {
    color: #fff;
    border-color: transparent;
}

.ms-choice--open.ms-choice--active {
    background: var(--m-blue);
    box-shadow: 0 5px 12px -5px rgba(25, 118, 210, 0.6);
}

.ms-choice--progress.ms-choice--active {
    background: var(--m-orange);
    box-shadow: 0 5px 12px -5px rgba(192, 105, 28, 0.6);
}

.ms-choice--completed.ms-choice--active {
    background: var(--m-green);
    box-shadow: 0 5px 12px -5px rgba(22, 128, 75, 0.6);
}

.ms-choice--canceled.ms-choice--active,
.ms-choice--high.ms-choice--active {
    background: var(--m-red);
    box-shadow: 0 5px 12px -5px rgba(199, 71, 71, 0.6);
}

.ms-choice--normal.ms-choice--active {
    background: var(--m-navy);
    box-shadow: 0 5px 12px -5px rgba(13, 44, 109, 0.6);
}

.ms-choice--low.ms-choice--active {
    background: var(--m-slate);
    box-shadow: 0 5px 12px -5px rgba(100, 116, 139, 0.6);
}

.ms-status-note {
    margin-top: 4px;
}

/* ── FORM GRID ── */
.ms-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 13px;
}

.ms-input--wide {
    grid-column: 1 / -1;
}

::v-deep .ms-input .v-input__slot {
    border-radius: 9px !important;
    background: #fff !important;
}

::v-deep .ms-input .v-label {
    font-size: 0.87rem;
}

::v-deep .ms-input--ltr input {
    direction: ltr;
    text-align: right;
    font-feature-settings: "tnum" 1;
}

/* ── ERROR ── */
.ms-error {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-top: 14px;
    padding: 10px 13px;
    border-radius: 10px;
    background: var(--m-red-soft);
    border: 1px solid rgba(199, 71, 71, 0.22);
    color: var(--m-red);
    font-size: 0.85rem;
    font-weight: 700;
}

.ms-error ::v-deep .v-icon {
    color: var(--m-red) !important;
}

/* ── FOOTER ── */
.ms-dlg-footer {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 13px 18px;
    background: #fff;
    border-top: 1px solid var(--m-border-soft);
}

.ms-footer-hint {
    color: var(--m-text-mute);
    font-size: 0.78rem;
}

.ms-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 20px;
    border-radius: 9px;
    font-family: inherit;
    font-size: 0.86rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-btn:disabled {
    opacity: 0.55;
    cursor: default;
    box-shadow: none;
}

.ms-btn--primary {
    background: var(--m-navy);
    border: 1px solid var(--m-navy);
    color: #fff;
}

.ms-btn--primary:hover:not(:disabled) {
    background: #142f78;
    box-shadow: 0 6px 14px -5px rgba(13, 44, 109, 0.55);
}

.ms-btn--primary ::v-deep .v-icon {
    color: #fff !important;
}

.ms-btn--ghost {
    background: transparent;
    border: 1px solid var(--m-border);
    color: var(--m-text-soft);
}

.ms-btn--ghost:hover:not(:disabled) {
    background: #f1f5f9;
    color: var(--m-text);
}

@media (max-width: 700px) {
    .ms-grid {
        grid-template-columns: 1fr;
    }
}
</style>